import { useNavigate } from 'react-router-dom';
import React, { useState, useRef, useEffect } from 'react';
import { 
  FaSignOutAlt, 
  FaCrown, 
  FaRegUser,
  FaStar,
  FaInfoCircle,
  FaSyncAlt,
  FaExclamationTriangle,
  FaCode
} from "react-icons/fa";
import { supabase } from './data/supabaseClient';
import { InlineThemeSelector } from './Templates'; // Import from Templates
import './MenuBar.css';
import { useTranslation } from 'react-i18next';

// Developer user ID constant
const DEVELOPER_USER_ID = '3f3d24fd-cf1f-42c8-b12e-3cfa5948c17d';

function OptionMenu() {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [profileData, setProfileData] = useState({
    avatar_url: null,
    username: '',
    reviewRating: 0,
    hasReviews: false,
    reviewCount: 0,
    activityScore: 0,
    overallScore: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const navigate = useNavigate();
  const menuRef = useRef(null);

  // Fetch user profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Get current user session
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (userError) {
          throw new Error(userError.message);
        }
        
        if (!user) {
          console.log('No authenticated user found');
          setIsLoading(false);
          return;
        }
        
        // Store current user ID
        setCurrentUserId(user.id);
        
        // Fetch user profile data
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('avatar_url, username, activity_score, overall_score')
          .eq('id', user.id)
          .single();
          
        if (profileError) {
          if (profileError.code === 'PGRST116') {
            // Profile not found - create a new one
            console.log('Profile not found, creating default profile');
            const { error: createError } = await supabase
              .from('profiles')
              .insert({
                id: user.id,
                username: user.email?.split('@')[0] || 'User',
                avatar_url: null,
                activity_score: 0,
                overall_score: 0
              });
              
            if (createError) throw new Error(`Failed to create profile: ${createError.message}`);
            
            // Set default profile data
            setProfileData({
              avatar_url: null,
              username: user.email?.split('@')[0] || 'User',
              reviewRating: 0,
              hasReviews: false,
              reviewCount: 0,
              activityScore: 0,
              overallScore: 0
            });
            
            setIsLoading(false);
            return;
          } else {
            throw new Error(`Failed to fetch profile: ${profileError.message}`);
          }
        }

        // Also fetch user stats to get more rating information
        const { data: userStats, error: statsError } = await supabase
          .from('user_stats')
          .select('average_rating, total_reviews')
          .eq('user_id', user.id)
          .single();
          
        let averageRating = 0;
        let reviewCount = 0;
        let hasReviews = false;
        
        // Check if we have stats data with ratings
        if (!statsError && userStats && userStats.average_rating) {
          averageRating = parseFloat(userStats.average_rating);
          reviewCount = userStats.total_reviews ? parseInt(userStats.total_reviews) : 0;
          hasReviews = reviewCount > 0;
        } else {
          // Fallback to counting reviews directly
          const { data: reviewsData, error: reviewsError } = await supabase
            .from('reviews')
            .select('rating')
            .eq('reviewee_id', user.id);
            
          if (!reviewsError && reviewsData && reviewsData.length > 0) {
            const totalRating = reviewsData.reduce((sum, review) => sum + review.rating, 0);
            averageRating = totalRating / reviewsData.length;
            reviewCount = reviewsData.length;
            hasReviews = true;
          }
        }
        
        // Ensure we have numeric values for scores
        const activityScore = typeof profileData.activity_score === 'number' ? 
          profileData.activity_score : 
          parseFloat(profileData.activity_score) || 0;
          
        const overallScore = typeof profileData.overall_score === 'number' ? 
          profileData.overall_score : 
          parseFloat(profileData.overall_score) || 0;
        
        setProfileData({
          avatar_url: profileData.avatar_url,
          username: profileData.username,
          reviewRating: averageRating,
          hasReviews: hasReviews,
          reviewCount: reviewCount,
          activityScore: activityScore,
          overallScore: overallScore
        });

        // Check if we need to recalculate scores due to mismatch
        if (hasReviews && (activityScore === 0 || overallScore === 0)) {
          console.log('Rating exists but scores are 0 - triggering manual recalculation');
          
          // Define a separate function for recalculation
          const triggerRecalculation = async () => {
            try {
              // Try the calculate_overall_score function
              const { error } = await supabase.rpc('calculate_overall_score', {
                p_user_id: user.id
              });
              
              if (error) {
                console.warn('Error with calculate_overall_score:', error);
                // Try alternative function as fallback
                try {
                  const altResult = await supabase.rpc('recalculate_all_user_scores');
                  if (altResult.error) {
                    console.warn('Error with recalculate_all_user_scores:', altResult.error);
                  }
                } catch (innerError) {
                  console.warn('Error calling recalculate_all_user_scores:', innerError);
                }
              }
              
              // Wait a moment for calculation to take effect
              await new Promise(resolve => setTimeout(resolve, 500));
              
              // Refetch profile data
              try {
                const { data: updatedProfile, error: refetchError } = await supabase
                  .from('profiles')
                  .select('activity_score, overall_score')
                  .eq('id', user.id)
                  .single();
                  
                if (refetchError) {
                  console.warn('Error fetching updated profile:', refetchError);
                } else if (updatedProfile) {
                  setProfileData(prevData => ({
                    ...prevData,
                    activityScore: parseFloat(updatedProfile.activity_score) || 0,
                    overallScore: parseFloat(updatedProfile.overall_score) || 0
                  }));
                }
              } catch (refetchError) {
                console.warn('Error refetching profile:', refetchError);
              }
            } catch (recalcError) {
              console.warn('Failed to recalculate scores:', recalcError);
            }
          };
          
          // Call the recalculation function but don't await it
          // This prevents it from blocking the rest of the component setup
          triggerRecalculation().catch(error => {
            console.warn('Unhandled error in recalculation:', error);
          });
        }
      } catch (err) {
        console.error('Error loading profile data:', err);
        setError(err.message || 'Failed to load profile data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUserProfile();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate("/login");
    } catch (error) {
      console.error('Error signing out:', error);
      // Gracefully handle sign out error
      alert(t('errors.sign_out_failed', 'Sign out failed. Please try again.'));
    }
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const navigateTo = (path) => {
    navigate(path);
    setIsOpen(false);
  };
  
  // Function to manually refresh user scores
  const refreshScores = async () => {
    try {
      setIsRecalculating(true);
      setError(null);
      
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        throw new Error(userError.message);
      }
      
      if (!user) return;
      
      // Try to call the calculate_overall_score function
      const { error } = await supabase.rpc('calculate_overall_score', {
        p_user_id: user.id
      });
      
      if (error) {
        console.warn('Error refreshing scores:', error);
      }
      
      // Wait a moment for the calculation to take effect
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Refetch profile data
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('avatar_url, username, activity_score, overall_score')
        .eq('id', user.id)
        .single();
        
      if (profileError) {
        throw new Error(`Failed to fetch updated profile: ${profileError.message}`);
      }
        
      // Refetch reviews data
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('reviews')
        .select('rating')
        .eq('reviewee_id', user.id);
      
      if (reviewsError) {
        throw new Error(`Failed to fetch reviews: ${reviewsError.message}`);
      }
      
      // Calculate average rating if there are reviews
      let averageRating = 0;
      let hasReviews = false;
      
      if (reviewsData.length > 0) {
        const totalRating = reviewsData.reduce((sum, review) => sum + review.rating, 0);
        averageRating = totalRating / reviewsData.length;
        hasReviews = true;
      }
      
      setProfileData({
        avatar_url: profileData.avatar_url,
        username: profileData.username,
        reviewRating: averageRating,
        hasReviews: hasReviews,
        reviewCount: reviewsData.length,
        activityScore: parseFloat(profileData.activity_score) || 0,
        overallScore: parseFloat(profileData.overall_score) || 0
      });
    } catch (error) {
      console.error('Error refreshing data:', error);
      setError(error.message || 'Failed to refresh data');
    } finally {
      setIsRecalculating(false);
    }
  };

  // Resolve avatar URL - ensure it's a complete URL
  const getAvatarUrl = () => {
    if (!profileData.avatar_url) return null;
    
    // If it's already a full URL (starts with http or https)
    if (profileData.avatar_url.startsWith('http')) {
      return profileData.avatar_url;
    }
    
    // If it's a storage bucket URL
    if (profileData.avatar_url.startsWith('avatars/')) {
      // Get the Supabase URL from the environment or build the URL manually
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      return `${supabaseUrl}/storage/v1/object/public/${profileData.avatar_url}`;
    }
    
    return profileData.avatar_url;
  };

  // Render stars with a specific color
  const renderStars = (rating, color, name) => {
    return [1, 2, 3, 4, 5].map((value) => (
      <FaStar 
        key={`${name}-${value}`}
        className={`star-icon ${value <= Math.round(rating) ? 'active' : ''}`}
        style={{ color: value <= Math.round(rating) ? color : undefined }}
      />
    ));
  };

  return (
    <div className="user-menu" ref={menuRef}>
      <button 
        className="user-menu-button" 
        onClick={toggleMenu}
        aria-expanded={isOpen}
        aria-label="User menu"
      >
        {isLoading ? (
          <div className="avatar-placeholder"></div>
        ) : error ? (
          <FaExclamationTriangle className="user-icon" style={{ color: '#e53935' }} title={error} />
        ) : (
          profileData.avatar_url ? (
            <img 
              src={getAvatarUrl()} 
              alt="Profile" 
              className="user-avatar-img" 
              onError={(e) => {
                console.log("Image failed to load:", e);
                e.target.onerror = null;
                e.target.style.display = "none";
                // Show the fallback icon
                const fallbackIcon = document.createElement('span');
                fallbackIcon.className = 'user-icon-fallback';
                fallbackIcon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 496 512"><path d="M248 8C111 8 0 119 0 256s111 248 248 248 248-111 248-248S385 8 248 8zm0 96c48.6 0 88 39.4 88 88s-39.4 88-88 88-88-39.4-88-88 39.4-88 88-88zm0 344c-58.7 0-111.3-26.6-146.5-68.2 18.8-35.4 55.6-59.8 98.5-59.8 2.4 0 4.8.4 7.1 1.1 13 4.2 26.6 6.9 40.9 6.9 14.3 0 28-2.7 40.9-6.9 2.3-.7 4.7-1.1 7.1-1.1 42.9 0 79.7 24.4 98.5 59.8C359.3 421.4 306.7 448 248 448z"/></svg>';
                e.target.parentNode.appendChild(fallbackIcon);
              }}
            />
          ) : (
            <FaRegUser className="user-icon" />
          )
        )}
      </button>
      
      {isOpen && (
        <div className="user-menu-dropdown">
          {error ? (
            <div className="error-message">
              <FaExclamationTriangle style={{ marginRight: '8px' }} />
              <span>{t('errors.profile_load_error', 'Failed to load profile data')}</span>
              <button onClick={() => window.location.reload()} className="retry-button">
                {t('common.retry', 'Retry')}
              </button>
            </div>
          ) : (
            <>
              <div className="user-menu-header">
                
                {/* Multi-dimensional rating display */}
                <div className="ratings-container">
                  <div className="ratings-header">
                    <h4>{t('menu.ratings')}</h4>
                    <button 
                      className="refresh-scores-btn" 
                      onClick={refreshScores}
                      disabled={isRecalculating}
                      title={t('menu.refresh_scores_tooltip', 'Refresh your scores')}
                    >
                      <FaSyncAlt className={`refresh-icon ${isRecalculating ? 'spinning' : ''}`} />
                    </button>
                  </div>
                  
                  {/* Overall Score - First and most prominent */}
                  <div className="rating-section primary-rating">
                    <div className="rating-label">
                      <span>{t('menu.overall_score')}</span>
                      <FaInfoCircle className="info-icon" title={t('menu.overall_score_tooltip')} />
                    </div>
                    <div className="star-rating">
                      {renderStars(profileData.overallScore, 'var(--accent-color)', 'overall')}
                      <span className="rating-value">
                        {profileData.overallScore.toFixed(1)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="rating-components">
                    {/* Review Rating */}
                    <div className="rating-section secondary-rating">
                      <div className="rating-label">
                        <span>{t('menu.review_rating')}</span>
                        <FaInfoCircle className="info-icon" title={t('menu.review_rating_tooltip')} />
                      </div>
                      <div className="star-rating">
                        {renderStars(profileData.reviewRating, 'var(--light-color)', 'review')}
                        <span className="rating-value">
                          {profileData.hasReviews 
                            ? `${profileData.reviewRating.toFixed(1)} (${profileData.reviewCount})` 
                            : t('profile.no_ratings_yet')}
                        </span>
                      </div>
                    </div>
                    
                    {/* Activity Score */}
                    <div className="rating-section secondary-rating">
                      <div className="rating-label">
                        <span>{t('menu.activity_score')}</span>
                        <FaInfoCircle className="info-icon" title={t('menu.activity_score_tooltip')} />
                      </div>
                      <div className="star-rating">
                        {renderStars(profileData.activityScore, 'var(--light-color)', 'activity')}
                        <span className="rating-value">
                          {profileData.activityScore.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="user-menu-items">
                <button 
                  className="menu-item" 
                  onClick={() => navigateTo("/MyprofileForm")}
                >
                  <FaRegUser className="item-icon" />
                  <span>{t('menu.profile')}</span>
                </button>
                
                <button 
                  className="menu-item" 
                  onClick={() => navigateTo("/subscription-plans")}
                >
                  <FaCrown className="item-icon" />
                  <span>{t('menu.subscription_plans', 'Subscription Plans')}</span>
                </button>
                
                {/* Theme selector - collapsible */}
                <div className="menu-divider"></div>
                <div className="theme-selector-section">
                  <InlineThemeSelector />
                </div>
                
                {/* Developer menu item - only show for specific user */}
                {currentUserId === DEVELOPER_USER_ID && (
                  <>
                    <div className="menu-divider"></div>
                    <button 
                      className="menu-item developer-item" 
                      onClick={() => navigateTo("/developer")}
                    >
                      <FaCode className="item-icon" />
                      <span>Developer</span>
                    </button>
                  </>
                )}
                
                <div className="menu-divider"></div>
                
                <button 
                  className="menu-item logout-item" 
                  onClick={handleSignOut}
                >
                  <FaSignOutAlt className="item-icon" />
                  <span>{t('menu.logout')}</span>
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default OptionMenu;